import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  OverlayView,
  InfoWindow,
} from "@react-google-maps/api";
import logoDTS from "./assets/logoData.png";

const GOOGLE_MAPS_API_KEY = "AIzaSyB9g0yILcgsg6xDCGHIeSeGzmGEK-XcwYg";
const GOOGLE_MAPS_LIBRARIES = ["drawing", "geometry", "visualization"];

const MAP_CONTAINER_STYLE = { height: "100%", width: "100%" };
const MAP_OPTIONS = {
  zoomControl: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
};

const globalStyles = `
  body, html {
    margin: 0; padding: 0; width: 100%; height: 100%;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    background-color: #f4f6f8;
  }

  .app-bar {
    background-color: #050936;
    height: 60px;
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0 20px;
    box-sizing: border-box;
    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    position: fixed;
    top: 0;
    z-index: 2000;
  }

  .logo-container {
    display: flex;
    align-items: center;
    gap: 20px;
    color: white;
    font-weight: bold;
    font-size: 18px;
  }

  .logo-img { height: 45px; width: auto; }

  .main-content {
    margin-top: 60px;
    height: calc(100vh - 60px);
    width: 100vw;
    position: relative;
    background-color: #eef2f6;
  }

  .live-badge {
    position: absolute;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    padding: 10px 20px;
    border-radius: 30px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    color: #374151;
    font-size: 14px;
  }

  .live-dot {
    width: 10px; height: 10px; background-color: #10b981;
    border-radius: 50%; animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
    100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
  }

  .status-container { display: flex; justify-content: center; align-items: center; height: 100%; width: 100%; }
  .status-card {
    background: white; padding: 40px 30px; border-radius: 20px; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    text-align: center; max-width: 420px; width: 100%; display: flex; flex-direction: column; align-items: center; gap: 15px; animation: fadeIn 0.5s ease-out;
  }
  .status-card h2 { margin: 0; color: #1f2937; font-size: 24px; }
  .status-card p { margin: 0; color: #6b7280; font-size: 16px; line-height: 1.6; }

  .icon-wrapper { width: 80px; height: 80px; display: flex; justify-content: center; align-items: center; border-radius: 50%; margin-bottom: 10px; }
  .icon-expired { background: #fff0ed; color: #ff5252; }
  .icon-error { background: #fef2f2; color: #ef4444; }

  .spinner {
    width: 60px; height: 60px; border: 4px solid #f3f4f6; border-top: 4px solid #3b82f6;
    border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 15px;
  }
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

  .marker-container {
    position: relative;
    width: 60px;
    height: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .truck-circle {
    width: 44px;
    height: 44px;
    background-color: #050936;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    box-shadow: 0 4px 8px rgba(0,0,0,0.3);
    border: 2px solid white;
    z-index: 2;
  }

  .truck-icon { font-size: 22px; }

  .direction-wrapper {
    position: absolute;
    width: 56px;
    height: 56px;
    top: 50%;
    left: 50%;
    margin-top: -28px;
    margin-left: -28px;
    z-index: 1;
  }

  .direction-pointer {
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 14px solid;
    filter: drop-shadow(0px 2px 2px rgba(0,0,0,0.3));
  }

  .truck-plate {
    background-color: white; color: #1f2937; padding: 2px 8px; border-radius: 6px;
    font-size: 11px; font-weight: bold; border: 1px solid #e5e7eb;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1); white-space: nowrap;
    position: absolute;
    bottom: -15px;
    z-index: 3;
  }
`;

const getPointerColor = (vehiculo) => {
  if (vehiculo.velocidad === 0 && vehiculo.encendido === 1) return "#eab308";
  if (vehiculo.velocidad > 0) return "#10b981";
  return "#ef4444";
};

const URL_AUTH =
  "https://gestiondeflota.boltrack.net/apirastreogestion/api/autenticacion/get-body-seguimiento";
const URL_GESTION = "https://gestiondeflota.boltrack.net/api/datos";

export default function TrackingMap() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  const [vehicles, setVehicles] = useState([]);
  const [mapCenter, setMapCenter] = useState({ lat: -17.7833, lng: -63.1821 });
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    language: "es",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (mapRef.current && mapCenter) {
      mapRef.current.panTo(mapCenter);
    }
  }, [mapCenter]);

  useEffect(() => {
    let intervalId;
    let revalidarIntervalId;

    const validarAuth = async () => {
      const authResponse = await fetch(URL_AUTH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: token }),
      });

      if (!authResponse.ok) {
        let msg = "Error de autenticación";
        try {
          const errorData = await authResponse.json();
          msg = errorData.message || msg;
        } catch (_) {}
        return { ok: false, msg };
      }

      const authData = await authResponse.json();

      if (authData.code !== "SUCCESS") {
        return { ok: false, msg: authData.message || "Hubo un problema al validar la información." };
      }

      const { fechaHoraInicio, fechaHoraFin, listaUnidades } = authData.body;
      const now = new Date();
      const start = new Date(fechaHoraInicio);
      const end = new Date(fechaHoraFin);

      if (now < start || now > end) {
        return { ok: false, expired: true };
      }

      return { ok: true, listaUnidades };
    };

    const iniciarTracking = async () => {
      try {
        const result = await validarAuth();

        if (!result.ok) {
          if (result.expired) {
            setStatus("expired");
          } else {
            setStatus("error");
            setErrorMessage(result.msg);
          }
          return;
        }

        setStatus("active");
        const placas = result.listaUnidades.split(",");

        const fetchUbicaciones = async () => {
          const nuevasUbicaciones = await Promise.all(
            placas.map(async (placa) => {
              try {
                const response = await fetch(URL_GESTION, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    data_op: {
                      placa: placa.trim(),
                      token: token,
                      checkConsumoUM: true,
                    },
                    op: "READ-OBTENEREVENTOACTUALVEHICULO",
                  }),
                });

                const evento = await response.json();

                if (evento.error === false && evento.data.length > 0) {
                  const dataVehiculo = evento.data[0];
                  return {
                    id: placa.trim(),
                    lat: parseFloat(dataVehiculo.LATITUD),
                    lng: parseFloat(dataVehiculo.LONGITUD),
                    velocidad: dataVehiculo.VELOCIDAD || 0,
                    encendido: dataVehiculo.I_ENCENDIDO || 0,
                    orient: dataVehiculo.ORIENT || 0,
                  };
                }
              } catch (error) {
                console.error(`Error obteniendo ubicación de ${placa}:`, error);
              }
              return null;
            }),
          );

          const ubicacionesValidas = nuevasUbicaciones.filter(
            (v) => v !== null,
          );
          setVehicles(ubicacionesValidas);

          if (ubicacionesValidas.length > 0) {
            const ultimoCamion =
              ubicacionesValidas[ubicacionesValidas.length - 1];
            setMapCenter({ lat: ultimoCamion.lat, lng: ultimoCamion.lng });
          }
        };

        fetchUbicaciones();
        intervalId = setInterval(fetchUbicaciones, 10000);

        revalidarIntervalId = setInterval(async () => {
          try {
            const revalidacion = await validarAuth();
            if (!revalidacion.ok) {
              clearInterval(intervalId);
              clearInterval(revalidarIntervalId);
              if (revalidacion.expired) {
                setStatus("expired");
              } else {
                setStatus("error");
                setErrorMessage(revalidacion.msg);
              }
            }
          } catch (error) {
            console.error("Error revalidando token:", error);
          }
        }, 30000);

      } catch (error) {
        console.error("Error general:", error);
        setStatus("error");
        setErrorMessage("Error de conexión. Verifique su conexión a internet.");
      }
    };

    if (token) {
      iniciarTracking();
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (revalidarIntervalId) clearInterval(revalidarIntervalId);
    };
  }, [token]);

  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);

  return (
    <>
      <style>{globalStyles}</style>

      <header className="app-bar">
        <div className="logo-container">
          <img src={logoDTS} alt="Boltrack Logo" className="logo-img" />
          <span> Tracking</span>
        </div>
      </header>

      <main className="main-content">
        {status === "loading" && (
          <div className="status-container">
            <div className="status-card">
              <div className="spinner"></div>
              <h2>Conectando con Boltrack</h2>
              <p>Localizando los vehículos en tiempo real...</p>
            </div>
          </div>
        )}

        {status === "expired" && (
          <div className="status-container">
            <div className="status-card">
              <div className="icon-wrapper icon-expired">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <h2>Enlace Expirado</h2>
              <p>El tiempo de seguimiento ha finalizado.</p>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="status-container">
            <div className="status-card">
              <div className="icon-wrapper icon-error">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <h2>Enlace no válido</h2>
              <p>{errorMessage}</p>
            </div>
          </div>
        )}

        {status === "active" && !isLoaded && (
          <div className="status-container">
            <div className="status-card">
              <div className="spinner"></div>
              <h2>Cargando mapa...</h2>
            </div>
          </div>
        )}

        {status === "active" && isLoaded && (
          <div style={{ height: "100%", width: "100%", position: "relative" }}>
            <div className="live-badge">
              <span className="live-dot"></span> Seguimiento Activo
            </div>

            <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              center={mapCenter}
              zoom={15}
              options={MAP_OPTIONS}
              onLoad={onMapLoad}
            >
              {vehicles.map((vehiculo) => (
                <OverlayView
                  key={vehiculo.id}
                  position={{ lat: vehiculo.lat, lng: vehiculo.lng }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={() => ({ x: -30, y: -30 })}
                >
                  <div
                    className="marker-container"
                    onClick={() => setSelectedVehicleId(vehiculo.id)}
                  >
                    <div
                      className="direction-wrapper"
                      style={{ transform: `rotate(${vehiculo.orient}deg)` }}
                    >
                      <div
                        className="direction-pointer"
                        style={{ borderBottomColor: getPointerColor(vehiculo) }}
                      ></div>
                    </div>

                    <div className="truck-circle">
                      <span className="truck-icon">🚛</span>
                    </div>

                    <span className="truck-plate">{vehiculo.id}</span>
                  </div>
                </OverlayView>
              ))}

              {selectedVehicle && (
                <InfoWindow
                  position={{
                    lat: selectedVehicle.lat,
                    lng: selectedVehicle.lng,
                  }}
                  onCloseClick={() => setSelectedVehicleId(null)}
                >
                  <div style={{ textAlign: "center" }}>
                    <strong>Unidad: {selectedVehicle.id}</strong>
                    <br />
                    Velocidad: {selectedVehicle.velocidad} km/h
                    <br />
                    Estado:{" "}
                    {selectedVehicle.velocidad > 0
                      ? "Encendido"
                      : selectedVehicle.encendido
                        ? "Ralenti"
                        : "Apagado"}
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </div>
        )}
      </main>
    </>
  );
}
